import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";
import { Checkbox } from "~/components/ui/checkbox";
import { FormField, FormItem, FormMessage } from "~/components/ui/form";
import { Text } from "~/components/text";
import LoadingSpinner from "~/components/loading/loading-spinner";
import { FilterSectionAction } from "./filter-section-action";
import { useLazyFacetItems } from "./hooks/use-lazy-facet-items";

export const CheckboxSection = ({
  items,
  name,
  label,
  noSuggestionsFound,
  tooltip,
  onSelectAll,
  onDeselectAll,
  selectAllDisabled,
}: {
  items: string[];
  name: string;
  label: string;
  noSuggestionsFound?: string;
  tooltip?: ReactNode;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  selectAllDisabled?: boolean;
}) => {
  if (onSelectAll && onDeselectAll) {
    throw new Error(
      "CheckboxSection: onSelectAll and onDeselectAll are mutually exclusive",
    );
  }
  const { control } = useFormContext();
  const { t } = useTranslation(["books"]);
  const { displayedItems, hasMore, scrollContainerRef } = useLazyFacetItems({
    allItems: items,
  });

  const selectAction = onSelectAll ?? onDeselectAll;
  const selectLabel = onDeselectAll
    ? t("books:filters.multiSelect.deselectAll")
    : t("books:filters.multiSelect.selectAll");

  const isDisabled = !!noSuggestionsFound || selectAllDisabled;

  const selectAllButton = selectAction && (
    <FilterSectionAction
      label={selectLabel}
      disabled={isDisabled}
      disabledTooltip={
        selectAllDisabled && onSelectAll
          ? t("books:filters.multiSelect.selectAllDisabled")
          : undefined
      }
      testId={`select-all-${name}`}
      onClick={selectAction}
    />
  );

  if (noSuggestionsFound) {
    return (
      <div className="flex flex-col gap-y-2 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-x-1">
            <Text variant="muted">{label}</Text>
            {tooltip}
          </div>
          {selectAllButton}
        </div>
        <Text
          as="div"
          variant="body"
          className="flex w-full justify-center text-center py-2"
        >
          {noSuggestionsFound}
        </Text>
      </div>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <div className="flex flex-col gap-y-2 py-2">
      <div className="flex items-center justify-between px-6">
        <div className="flex items-center gap-x-1">
          <Text variant="muted">{label}</Text>
          {tooltip}
        </div>
        {selectAllButton}
      </div>
      <FormField
        control={control}
        name={`filters.${name}.values`}
        render={({ field }) => {
          return (
            <FormItem
              ref={scrollContainerRef}
              className="max-h-64 w-full overflow-y-auto"
            >
              {displayedItems.map((item) => {
                const isChecked = field.value?.includes(item);
                return (
                  <label
                    htmlFor={`checkbox-filter-${item}`}
                    key={item}
                    data-testid={`checkbox-filter-${item}`}
                    className="flex flex-row items-center w-full space-x-3 space-y-0 px-6 py-1 mt-0 hover:bg-secondary-100 cursor-pointer"
                  >
                    <Checkbox
                      checked={isChecked}
                      className="ml-1 mr-3"
                      id={`checkbox-filter-${item}`}
                      // Keep focus on the checkbox so keyboard users can toggle it with Space.
                      onMouseDown={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                      }}
                      onCheckedChange={(checked) => {
                        field.onChange(
                          checked
                            ? [...(field.value ?? []), item]
                            : field.value?.filter(
                                (value: string) => value !== item,
                              ),
                        );
                      }}
                    />
                    <Text as="span" variant="body">
                      {item}
                    </Text>
                  </label>
                );
              })}
              {hasMore && (
                <div className="flex justify-center py-2">
                  <LoadingSpinner size="xs" />
                </div>
              )}
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
};
